/**
 * An in-memory `TasksSource` — what `/__ui/tasks` and every unit test run against, with no
 * server and no database.
 *
 * It is deliberately *not* a re-implementation of `services/tasks/queries.ts`: it filters,
 * groups and counts just enough for the page to be driven, and the real grouping rules are the
 * server's. What it does reproduce exactly is the **shape** of the contract, so a page that
 * renders here renders against the api.
 *
 * ## Round T4: the fixture had to get bigger before the phone defect could be seen
 *
 * The five-row fixture round T2 shipped rendered a phone page that fitted on one screen, and
 * the user's report — *"almost unusable on mobile: giant checkboxes and big priority and date
 * labels"* — is about a list, not about five rows. So the list is now **31 personal rows across
 * all five due buckets**, 12 delegations and 9 archived tasks, with the properties that make a
 * row wide: long item names, long status labels (`Working on it` is 13 characters), four boards
 * with long names, subitem breadcrumbs, overdue dates and a person on every row.
 *
 * Nothing here is a capture of monday — monday has no Tasks section — so the *content* is
 * invented on purpose and only the **shape** is the contract's (`tasks/T-T4.md` §ASSUMED).
 */
import { statusColorHex } from '@elizmonday/ui';
import {
  type TaskCountsView,
  type TaskGroupView,
  type TaskItemContext,
  type TaskGroupPageInput,
  type TaskGroupPageView,
  type TaskGroupedListInput,
  type TaskListByGroupResultView,
  type TaskListInput,
  type TaskListResultView,
  type TaskMappingOptionsView,
  type TaskMappingView,
  type TaskRowView,
  type TaskColumnWidthsView,
  type TaskSettingsView,
  type TasksSource,
} from './types';
import { coerceTaskColumnWidths, taskColumnWidthsToView } from './column-widths';
import type {
  TaskInsightsView,
  TaskMemberInsightView,
  TaskTimelineView,
} from './insights/metrics';
import type { TasksInsightsSource } from './insights/source';
import { TASK_BUCKET_LABELS } from './vocabulary';

const ME = { id: '5000000000001', name: 'Roberto Elizeta', photoUrl: null };
const ADA = { id: '5000000000002', name: 'Ada Byron', photoUrl: null };
const GRACE = { id: '5000000000003', name: 'Grace Hopper', photoUrl: null };
const NO_ACCESS = { id: '5000000000004', name: 'Linus Ext', photoUrl: null };
const KATE = { id: '5000000000005', name: 'Katherine Johnson', photoUrl: null };

/** A day offset from a fixed "today", so the fixture never drifts with the calendar. */
const TODAY = new Date('2026-09-15T00:00:00.000Z');
const day = (offset: number) => new Date(TODAY.getTime() + offset * 86_400_000).toISOString();

/** The four boards the fixture spreads across; the names are as long as a real board's. */
const BOARDS = {
  marketing: { id: '7000000000001', name: 'Marketing plan' },
  subitems: { id: '7000000000002', name: 'Subitems of Marketing plan' },
  website: { id: '7000000000003', name: 'Website' },
  launch: { id: '7000000000004', name: 'Q4 product launch' },
  onboarding: { id: '7000000000005', name: 'Client onboarding 2026' },
} as const;

/**
 * Two workspaces, so the filter bar's **workspace** picker (T8, P-274) has something to pick
 * between on `/__ui/tasks`. Fixture shape, like every other value in this file.
 */
export const FIXTURE_WORKSPACES: Readonly<Record<string, string>> = {
  w1: 'Main workspace',
  w2: 'Client work',
};

export const FIXTURE_WORKSPACE_OF: Readonly<Record<string, string>> = {
  [BOARDS.marketing.id]: 'w1',
  [BOARDS.subitems.id]: 'w1',
  [BOARDS.website.id]: 'w1',
  [BOARDS.launch.id]: 'w1',
  [BOARDS.onboarding.id]: 'w2',
};

/**
 * The fixture's stand-in for the boards' `labels_colors` (P-267).
 *
 * `tasks.list` resolves `statusColor` from the mapped status column's settings, so a fixture that
 * left it null would draw a grey page and the colour defect would still be invisible on
 * `/__ui/tasks`. These five are monday's own palette colours for the five labels the fixture uses
 * — `working_orange`, `done_green`, `stuck_red`, `dark_purple`, `bright_blue` — which is what a
 * board that never recoloured a label stores. They are resolved through `statusColorHex` rather
 * than written out as hexes, because U1 allows no raw hex outside the token package. It is
 * fixture *shape*, like every other value in this file, not a claim about any real board.
 */
export const FIXTURE_LABEL_COLORS: Readonly<Record<string, string>> = {
  'Working on it': statusColorHex('working_orange'),
  Done: statusColorHex('done_green'),
  Stuck: statusColorHex('stuck_red'),
  High: statusColorHex('dark_purple'),
  'Ready for review': statusColorHex('bright_blue'),
};

function row(partial: Partial<TaskRowView> & Pick<TaskRowView, 'id' | 'itemId' | 'itemName'>): TaskRowView {
  const merged: TaskRowView = {
    boardId: BOARDS.marketing.id,
    boardName: BOARDS.marketing.name,
    parentItemId: null,
    parentItemName: null,
    assignee: ME,
    assigner: ADA,
    assignedAt: day(-3),
    assignmentSource: 'user',
    statusLabel: 'Working on it',
    statusColor: null,
    isDone: false,
    doneAt: null,
    dueAt: day(0),
    bucket: 'today',
    assigneeHasAccess: true,
    archived: false,
    /* P-301 · every row is a row nobody has archived by hand until the fixture says so. */
    archivedAt: null,
    ...partial,
  };
  /* After the spread, so a row that overrides `statusLabel` gets that label's colour. */
  if (partial.statusColor === undefined) {
    merged.statusColor =
      merged.statusLabel === null ? null : (FIXTURE_LABEL_COLORS[merged.statusLabel] ?? null);
  }
  return merged;
}

/** `{ boardId, boardName }` from one of the four, so a row names a board and never an id. */
const on = (board: { id: string; name: string }) => ({ boardId: board.id, boardName: board.name });

/**
 * **My Tasks** — 31 rows, every bucket populated, because the phone defect is a *list* defect.
 * The names, statuses and boards are the widest plausible ones: at 390 px the row has to decide
 * what it truncates, and a fixture of short names never makes it decide.
 */
const MINE: TaskRowView[] = [
  /* ── Overdue (7) ───────────────────────────────────────────────────────── */
  row({ id: '1', itemId: '2000000000001', itemName: 'Draft the Q4 launch brief', dueAt: day(-2), bucket: 'overdue', statusLabel: 'Stuck' }),
  row({ id: '31', itemId: '2000000000031', itemName: 'Chase the printer about the booth panels', dueAt: day(-9), bucket: 'overdue', statusLabel: 'High', ...on(BOARDS.launch), assigner: GRACE }),
  row({ id: '32', itemId: '2000000000032', itemName: 'Send the revised media plan to Ada', dueAt: day(-5), bucket: 'overdue', statusLabel: 'Working on it' }),
  row({ id: '33', itemId: '2000000000033', itemName: 'Fix the broken pricing anchor links', dueAt: day(-4), bucket: 'overdue', statusLabel: 'Stuck', ...on(BOARDS.website), assigner: KATE }),
  row({
    id: '34',
    itemId: '2000000000034',
    itemName: 'Collect the last two supplier quotes',
    parentItemId: '2000000000002',
    parentItemName: 'Book the venue',
    ...on(BOARDS.subitems),
    dueAt: day(-1),
    bucket: 'overdue',
    statusLabel: 'Working on it',
  }),
  row({ id: '35', itemId: '2000000000035', itemName: 'Reply to the onboarding questionnaire', dueAt: day(-12), bucket: 'overdue', statusLabel: null, ...on(BOARDS.onboarding), assigner: GRACE }),
  row({ id: '36', itemId: '2000000000036', itemName: 'Approve the out-of-home artwork', dueAt: day(-3), bucket: 'overdue', statusLabel: 'Ready for review', ...on(BOARDS.launch) }),

  /* ── Today (6) ─────────────────────────────────────────────────────────── */
  row({ id: '2', itemId: '2000000000002', itemName: 'Book the venue', bucket: 'today' }),
  row({ id: '37', itemId: '2000000000037', itemName: 'Run the weekly launch stand-up', bucket: 'today', statusLabel: 'Working on it', ...on(BOARDS.launch) }),
  row({ id: '38', itemId: '2000000000038', itemName: 'Publish the September changelog', bucket: 'today', statusLabel: 'Ready for review', ...on(BOARDS.website), assigner: KATE }),
  row({ id: '39', itemId: '2000000000039', itemName: 'Confirm the catering headcount', bucket: 'today', statusLabel: 'High', dueAt: day(0) }),
  row({
    id: '40',
    itemId: '2000000000040',
    itemName: 'Draft the welcome email sequence',
    parentItemId: '2000000000041',
    parentItemName: 'Client onboarding revamp',
    ...on(BOARDS.subitems),
    bucket: 'today',
    statusLabel: 'Working on it',
    assigner: GRACE,
  }),
  row({ id: '41', itemId: '2000000000041', itemName: 'Client onboarding revamp', bucket: 'today', statusLabel: 'Done', isDone: true, doneAt: day(0), ...on(BOARDS.onboarding) }),

  /* ── This week (7) ─────────────────────────────────────────────────────── */
  row({
    id: '3',
    itemId: '2000000000003',
    itemName: 'Collect the supplier quotes',
    parentItemId: '2000000000002',
    parentItemName: 'Book the venue',
    ...on(BOARDS.subitems),
    dueAt: day(3),
    bucket: 'this_week',
    assigner: GRACE,
  }),
  row({ id: '42', itemId: '2000000000042', itemName: 'Review the agency creative round two', dueAt: day(2), bucket: 'this_week', statusLabel: 'Ready for review', ...on(BOARDS.launch) }),
  row({ id: '43', itemId: '2000000000043', itemName: 'Update the investor one-pager', dueAt: day(4), bucket: 'this_week', statusLabel: 'Working on it', assigner: KATE }),
  row({ id: '44', itemId: '2000000000044', itemName: 'Migrate the blog to the new template', dueAt: day(5), bucket: 'this_week', statusLabel: 'Stuck', ...on(BOARDS.website) }),
  row({ id: '45', itemId: '2000000000045', itemName: 'Prepare the onboarding kick-off deck', dueAt: day(3), bucket: 'this_week', statusLabel: 'High', ...on(BOARDS.onboarding), assigner: GRACE }),
  row({ id: '46', itemId: '2000000000046', itemName: 'Sign off the launch photography brief', dueAt: day(6), bucket: 'this_week', statusLabel: null, ...on(BOARDS.launch) }),
  row({ id: '47', itemId: '2000000000047', itemName: 'Close the September ad invoices', dueAt: day(4), bucket: 'this_week', statusLabel: 'Done', isDone: true, doneAt: day(1) }),

  /* ── Later (7) ─────────────────────────────────────────────────────────── */
  row({ id: '4', itemId: '2000000000004', itemName: 'Rewrite the pricing page', ...on(BOARDS.website), dueAt: day(20), bucket: 'later', statusLabel: null }),
  row({ id: '48', itemId: '2000000000048', itemName: 'Plan the Q1 partner roadshow', dueAt: day(24), bucket: 'later', statusLabel: 'Working on it', ...on(BOARDS.launch), assigner: GRACE }),
  row({ id: '49', itemId: '2000000000049', itemName: 'Refresh the customer testimonial wall', dueAt: day(31), bucket: 'later', statusLabel: 'High', ...on(BOARDS.website) }),
  row({ id: '50', itemId: '2000000000050', itemName: 'Negotiate the 2027 venue rate card', dueAt: day(45), bucket: 'later', statusLabel: null, assigner: KATE }),
  row({ id: '51', itemId: '2000000000051', itemName: 'Draft the onboarding SLA appendix', dueAt: day(18), bucket: 'later', statusLabel: 'Ready for review', ...on(BOARDS.onboarding) }),
  row({
    id: '52',
    itemId: '2000000000052',
    itemName: 'Localise the launch landing page copy',
    parentItemId: '2000000000004',
    parentItemName: 'Rewrite the pricing page',
    ...on(BOARDS.subitems),
    dueAt: day(27),
    bucket: 'later',
    statusLabel: 'Working on it',
  }),
  row({ id: '53', itemId: '2000000000053', itemName: 'Archive the 2025 campaign reports', dueAt: day(60), bucket: 'later', statusLabel: 'Stuck', assigner: GRACE }),

  /* ── No date (4) ───────────────────────────────────────────────────────── */
  row({ id: '5', itemId: '2000000000005', itemName: 'Archive the 2025 assets', dueAt: null, bucket: 'no_date' }),
  row({ id: '54', itemId: '2000000000054', itemName: 'Collect ideas for the next brand film', dueAt: null, bucket: 'no_date', statusLabel: null, ...on(BOARDS.launch) }),
  row({ id: '55', itemId: '2000000000055', itemName: 'Audit the unused marketing integrations', dueAt: null, bucket: 'no_date', statusLabel: 'Working on it', ...on(BOARDS.website), assigner: KATE }),
  row({ id: '56', itemId: '2000000000056', itemName: 'Write the onboarding retrospective notes', dueAt: null, bucket: 'no_date', statusLabel: 'High', ...on(BOARDS.onboarding) }),
];

/** **Tasks Assigned** — the delegations, including the three the assignee cannot see (§4). */
const ASSIGNED: TaskRowView[] = [
  row({ id: '11', itemId: '2000000000011', itemName: 'Prepare the partner deck', assignee: ADA, assigner: ME, dueAt: day(-1), bucket: 'overdue' }),
  row({ id: '12', itemId: '2000000000012', itemName: 'Review the launch copy', assignee: GRACE, assigner: ME, dueAt: day(2), bucket: 'this_week', statusLabel: 'Done', isDone: true, doneAt: day(-1) }),
  row({ id: '13', itemId: '2000000000013', itemName: 'Sign off the agency contract', assignee: NO_ACCESS, assigner: ME, assigneeHasAccess: false, ...on(BOARDS.website), dueAt: day(5), bucket: 'later' }),
  row({ id: '14', itemId: '2000000000014', itemName: 'Book the photographer for the keynote', assignee: ADA, assigner: ME, dueAt: day(-6), bucket: 'overdue', statusLabel: 'Stuck', ...on(BOARDS.launch) }),
  row({ id: '15', itemId: '2000000000015', itemName: 'Chase the translation vendor', assignee: KATE, assigner: ME, dueAt: day(0), bucket: 'today', statusLabel: 'High' }),
  row({ id: '16', itemId: '2000000000016', itemName: 'Rebuild the newsletter template', assignee: GRACE, assigner: ME, dueAt: day(0), bucket: 'today', statusLabel: 'Working on it', ...on(BOARDS.website) }),
  row({ id: '17', itemId: '2000000000017', itemName: 'Collect the venue insurance certificate', assignee: NO_ACCESS, assigner: ME, assigneeHasAccess: false, dueAt: day(3), bucket: 'this_week', statusLabel: 'Ready for review', ...on(BOARDS.launch) }),
  row({ id: '18', itemId: '2000000000018', itemName: 'Draft the customer migration checklist', assignee: ADA, assigner: ME, dueAt: day(4), bucket: 'this_week', statusLabel: 'Working on it', ...on(BOARDS.onboarding) }),
  row({
    id: '19',
    itemId: '2000000000019',
    itemName: 'Re-shoot the product hero images',
    parentItemId: '2000000000004',
    parentItemName: 'Rewrite the pricing page',
    ...on(BOARDS.subitems),
    assignee: KATE,
    assigner: ME,
    dueAt: day(6),
    bucket: 'this_week',
    statusLabel: 'High',
  }),
  row({ id: '20', itemId: '2000000000020', itemName: 'Plan the reseller webinar series', assignee: GRACE, assigner: ME, dueAt: day(21), bucket: 'later', statusLabel: null, ...on(BOARDS.launch) }),
  row({ id: '22', itemId: '2000000000022', itemName: 'Refresh the onboarding welcome video', assignee: ADA, assigner: ME, dueAt: day(33), bucket: 'later', statusLabel: 'Working on it', ...on(BOARDS.onboarding) }),
  row({ id: '23', itemId: '2000000000023', itemName: 'Consolidate the agency invoices', assignee: NO_ACCESS, assigner: ME, assigneeHasAccess: false, dueAt: null, bucket: 'no_date', statusLabel: null }),
];

/** **Archive** — done long enough ago that the delay moved them (`docs/13` §2). */
const ARCHIVE: TaskRowView[] = [
  row({ id: '21', itemId: '2000000000021', itemName: 'Ship the summer newsletter', statusLabel: 'Done', isDone: true, doneAt: day(-30), dueAt: day(-32), bucket: 'overdue', archived: true }),
  row({ id: '24', itemId: '2000000000024', itemName: 'Close the August campaign report', statusLabel: 'Done', isDone: true, doneAt: day(-24), dueAt: day(-26), bucket: 'overdue', archived: true, ...on(BOARDS.launch) }),
  row({ id: '25', itemId: '2000000000025', itemName: 'Retire the legacy landing pages', statusLabel: 'Done', isDone: true, doneAt: day(-21), dueAt: day(-20), bucket: 'overdue', archived: true, ...on(BOARDS.website), assigner: KATE }),
  row({ id: '26', itemId: '2000000000026', itemName: 'Hand over the spring onboarding cohort', statusLabel: 'Done', isDone: true, doneAt: day(-19), dueAt: day(-19), bucket: 'overdue', archived: true, ...on(BOARDS.onboarding) }),
  row({
    id: '27',
    itemId: '2000000000027',
    itemName: 'Return the borrowed exhibition stands',
    parentItemId: '2000000000002',
    parentItemName: 'Book the venue',
    ...on(BOARDS.subitems),
    statusLabel: 'Done',
    isDone: true,
    doneAt: day(-18),
    dueAt: day(-17),
    bucket: 'overdue',
    archived: true,
  }),
  row({ id: '28', itemId: '2000000000028', itemName: 'Publish the July product update', statusLabel: 'Done', isDone: true, doneAt: day(-17), dueAt: day(-18), bucket: 'overdue', archived: true, ...on(BOARDS.website) }),
  row({ id: '29', itemId: '2000000000029', itemName: 'Wrap up the partner survey analysis', statusLabel: 'Done', isDone: true, doneAt: day(-16), dueAt: day(-15), bucket: 'overdue', archived: true, assigner: GRACE }),
  row({ id: '30', itemId: '2000000000030', itemName: 'Decommission the old CRM export job', statusLabel: 'Done', isDone: true, doneAt: day(-15), dueAt: null, bucket: 'no_date', archived: true, ...on(BOARDS.onboarding) }),
  row({ id: '57', itemId: '2000000000057', itemName: 'Close the Q2 launch retrospective', statusLabel: 'Done', isDone: true, doneAt: day(-15), dueAt: day(-16), bucket: 'overdue', archived: true, ...on(BOARDS.launch), assigner: KATE }),
];

const SETTINGS: TaskSettingsView = {
  archiveDelayDays: 14,
  digestFrequency: 'daily',
  digestHour: 8,
  remindOverdue: true,
  notifyOnComplete: true,
  tab: 'mine',
  groupBy: 'due',
  showArchived: false,
};

function groupRows(rows: TaskRowView[], groupBy: string): TaskGroupView[] {
  const buckets = new Map<string, TaskGroupView>();
  for (const entry of rows) {
    const key =
      groupBy === 'board'
        ? entry.boardId
        : groupBy === 'assignee'
          ? (entry.assignee?.id ?? 'none')
          : groupBy === 'assigner'
            ? (entry.assigner?.id ?? 'none')
            : groupBy === 'status'
              ? (entry.statusLabel ?? 'none')
              : entry.bucket;
    const label =
      groupBy === 'board'
        ? entry.boardName
        : groupBy === 'assignee'
          ? (entry.assignee?.name ?? 'Unassigned')
          : groupBy === 'assigner'
            ? (entry.assigner?.name ?? 'Nobody')
            : groupBy === 'status'
              ? (entry.statusLabel ?? 'No status')
              : TASK_BUCKET_LABELS[entry.bucket];
    const existing = buckets.get(key);
    if (existing) {
      existing.rows.push(entry);
      existing.count += 1;
    } else {
      buckets.set(key, { key, label, count: 1, rows: [entry] });
    }
  }
  return [...buckets.values()];
}

/**
 * P-305 · where the harness keeps the column widths, and the two functions that touch it.
 *
 * A private window, blocked site data or a value another tab corrupted all throw or answer
 * nonsense; the width of a column is never a reason to fail a page, so both directions swallow.
 */
export const FIXTURE_COLUMN_WIDTHS_KEY = 'elizmonday.tasks.columnWidths';

function readFixtureColumnWidths(): TaskColumnWidthsView {
  try {
    const raw = window.localStorage.getItem(FIXTURE_COLUMN_WIDTHS_KEY);
    return raw === null ? {} : taskColumnWidthsToView(coerceTaskColumnWidths(JSON.parse(raw)));
  } catch {
    return {};
  }
}

function writeFixtureColumnWidths(widths: TaskColumnWidthsView): void {
  try {
    window.localStorage.setItem(
      FIXTURE_COLUMN_WIDTHS_KEY,
      JSON.stringify(taskColumnWidthsToView(coerceTaskColumnWidths(widths))),
    );
  } catch {
    // Same reasoning as `writeSavedViews`: losing it costs a reload's worth of layout.
  }
}

export interface FixtureTasksOptions {
  /** Start with nothing, so the harness can show the empty state (`?screen=empty`). */
  empty?: boolean;
}

export function createFixtureTasksSource(options: FixtureTasksOptions = {}): TasksSource {
  let settings: TaskSettingsView = { ...SETTINGS };
  const sent: { itemId: string; assigneeId: string; message?: string }[] = [];
  /** P-294 — what `setMapping` has changed, per board, for this source instance. */
  const fixtureMapping: Record<string, TaskMappingView> = {};
  /**
   * **P-301** — the manual archive, per item, for this source instance.
   *
   * It is a store and not a flag on the constant rows because the harness has to survive the
   * whole round trip: archive three rows, see them leave My Tasks, find them in the Archive tab
   * and unarchive them again. A fixture that answered the same frozen arrays would prove only
   * that the buttons exist, which is the thing the 2026-09-14 rule forbids counting as green.
   */
  const archivedAt: Record<string, string> = {};

  /** The constants, with whatever this session has archived applied on top. */
  const withArchive = (rows: readonly TaskRowView[]): TaskRowView[] =>
    rows.map((entry) =>
      archivedAt[entry.itemId] === undefined
        ? entry
        : { ...entry, archivedAt: archivedAt[entry.itemId]!, archived: true },
    );

  const rowsFor = (tab: string): TaskRowView[] => {
    if (options.empty) return [];
    /* The Archive tab is the **union** (P-301 §3): the computed rows plus whatever was archived. */
    if (tab === 'archive') {
      const manual = [...withArchive(MINE), ...withArchive(ASSIGNED)].filter(
        (entry) => entry.archivedAt !== null,
      );
      return [...ARCHIVE, ...manual];
    }
    const base = tab === 'assigned' ? ASSIGNED : MINE;
    /* Every other tab hides them, exactly as `loadRows` negates the same union. */
    return withArchive(base).filter((entry) => entry.archivedAt === null);
  };

  /** The rows one question asks for — shared by the flat list and the per-group one (P-318). */
  const askedFor = (input: TaskGroupedListInput): TaskRowView[] => {
    let rows = rowsFor(input.tab ?? 'mine');
    const filters = input.filters ?? {};
    if (filters.query) {
      const needle = filters.query.toLowerCase();
      rows = rows.filter((entry) => entry.itemName.toLowerCase().includes(needle));
    }
    if (filters.done === 'open') rows = rows.filter((entry) => !entry.isDone);
    if (filters.done === 'done') rows = rows.filter((entry) => entry.isDone);
    if (filters.boardIds && filters.boardIds.length > 0) {
      rows = rows.filter((entry) => filters.boardIds?.includes(entry.boardId));
    }
    return rows;
  };

  return {
    async list(input: TaskListInput): Promise<TaskListResultView> {
      const rows = askedFor(input);
      return {
        groups: groupRows(rows, input.groupBy ?? settings.groupBy),
        rows,
        nextCursor: null,
        total: rows.length,
        settings,
      };
    },

    /**
     * **P-318** — the per-group answer, paged for real.
     *
     * The harness pages rather than answering every row in one go, so `/__ui/tasks` exercises the
     * control the card is about: a fixture that always answered `nextCursor: null` would prove
     * only that the page *can* draw a group, which is the thing the 2026-09-14 rule forbids
     * counting as green.
     */
    async listByGroup(input: TaskGroupedListInput): Promise<TaskListByGroupResultView> {
      const rows = askedFor(input);
      const limit = input.limit ?? 50;
      return {
        groups: groupRows(rows, input.groupBy ?? settings.groupBy).map((group) => ({
          key: group.key,
          label: group.label,
          count: group.count,
          rows: group.rows.slice(0, limit),
          nextCursor: group.rows.length > limit ? (group.rows[limit - 1]?.id ?? null) : null,
        })),
        total: rows.length,
        settings,
      };
    },

    /** **P-318** — one more page of one group, continued from that group's own cursor. */
    async listGroup(input: TaskGroupPageInput): Promise<TaskGroupPageView> {
      const limit = input.limit ?? 50;
      const groups = groupRows(askedFor(input), input.groupBy ?? settings.groupBy);
      const group = groups.find((entry) => entry.key === input.groupKey);
      /* A group this answer no longer has is empty and finished, never an error (queries.ts). */
      if (!group) {
        return { key: input.groupKey, label: input.groupKey, count: 0, rows: [], nextCursor: null };
      }
      const at = input.cursor ? group.rows.findIndex((entry) => entry.id === input.cursor) : -1;
      /* A cursor whose row has moved is finished too, so a client cannot loop on it. */
      if (input.cursor && at < 0) {
        return { key: group.key, label: group.label, count: group.count, rows: [], nextCursor: null };
      }
      const page = group.rows.slice(at + 1, at + 1 + limit);
      return {
        key: group.key,
        label: group.label,
        count: group.count,
        rows: page,
        nextCursor: at + 1 + limit < group.rows.length ? (page.at(-1)?.id ?? null) : null,
      };
    },

    /**
     * The fixture's facets — the same shape `tasks.facets` answers, over the tab's whole row set
     * and never over the filtered one, so the harness exercises the real P-274 behaviour.
     */
    async facets({ tab }: { tab: string; includeArchived: boolean }) {
      const rows = rowsFor(tab);
      const boards = new Map<string, { id: string; name: string; workspaceId: string }>();
      const assignees = new Map<string, string>();
      const assigners = new Map<string, string>();
      const workspaces = new Map<string, { id: string; name: string }>();
      const statusLabels = new Set<string>();
      for (const entry of rows) {
        const workspaceId = FIXTURE_WORKSPACE_OF[entry.boardId] ?? 'w1';
        boards.set(entry.boardId, { id: entry.boardId, name: entry.boardName, workspaceId });
        workspaces.set(workspaceId, { id: workspaceId, name: FIXTURE_WORKSPACES[workspaceId] ?? 'Workspace' });
        if (entry.assignee) assignees.set(entry.assignee.id, entry.assignee.name ?? entry.assignee.id);
        if (entry.assigner) assigners.set(entry.assigner.id, entry.assigner.name ?? entry.assigner.id);
        if (entry.statusLabel) statusLabels.add(entry.statusLabel);
      }
      const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
      const people = (map: Map<string, string>) =>
        [...map.entries()].map(([id, name]) => ({ id, name })).sort(byName);
      return {
        workspaces: [...workspaces.values()].sort(byName),
        boards: [...boards.values()].sort(byName),
        assignees: people(assignees),
        assigners: people(assigners),
        statusLabels: [...statusLabels].sort((a, b) => a.localeCompare(b)),
      };
    },

    async counts(): Promise<TaskCountsView> {
      if (options.empty) {
        return { mine: 0, mineOpen: 0, assigned: 0, archive: 0, overdue: 0, dueToday: 0, withoutAccess: 0 };
      }
      /*
       * P-301 · through `rowsFor`, so archiving a row really does drop the tab count and the
       * rail badge, and the Archive count really does go up. Counting the frozen arrays would
       * have made the badge disagree with the list the moment anything was archived.
       */
      const mine = rowsFor('mine');
      const assigned = rowsFor('assigned');
      return {
        mine: mine.length,
        mineOpen: mine.filter((entry) => !entry.isDone).length,
        assigned: assigned.length,
        archive: rowsFor('archive').length,
        overdue: mine.filter((entry) => entry.bucket === 'overdue').length,
        dueToday: mine.filter((entry) => entry.bucket === 'today').length,
        withoutAccess: assigned.filter((entry) => !entry.assigneeHasAccess).length,
      };
    },

    async settings(): Promise<TaskSettingsView> {
      return settings;
    },

    /*
     * **P-305** — the column widths, in this browser's `localStorage`.
     *
     * Every other fixture member is in-memory, and this one deliberately is not: the card's
     * acceptance is *"drag Item +200 … reload keeps it"*, and a harness that forgot on reload
     * could only ever prove that the drag paints. `localStorage` is what stands in for
     * `user_preferences` where there is no api, exactly as `state.ts` does for saved views; the
     * real page writes `preferences.setMany` and never touches this.
     */
    async columnWidths(): Promise<TaskColumnWidthsView> {
      return readFixtureColumnWidths();
    },

    async saveColumnWidths(widths): Promise<TaskColumnWidthsView> {
      writeFixtureColumnWidths(widths);
      return readFixtureColumnWidths();
    },

    async saveSettings(patch): Promise<TaskSettingsView> {
      settings = { ...settings, ...patch };
      return settings;
    },

    async nudge(input): Promise<{ updateId: string }> {
      sent.push(input);
      return { updateId: `3000000000${sent.length}` };
    },

    async mapping({ boardId }): Promise<TaskMappingView> {
      return fixtureMapping[boardId] ?? {
        boardId,
        assigneeColumnIds: ['people'],
        statusColumnId: 'status',
        dueColumnId: 'date4',
        doneLabels: ['1'],
        overridden: false,
        overriddenBy: null,
        detectedAt: day(-10),
      };
    },

    /**
     * **P-294.** The fixture board carries the shape the incident was made of: *two* status-type
     * columns, `Priority` first and `Status` second, both with monday's default
     * `done_colors: [1]`. The panel must offer both and must not pretend the first one is the
     * status.
     */
    async mappingOptions({ boardId }): Promise<TaskMappingOptionsView> {
      return {
        boardId,
        assigneeColumns: [
          { id: 'people', title: 'Owner', type: 'people' },
          { id: 'person', title: 'Reviewer', type: 'person' },
        ],
        statusColumns: [
          {
            id: 'status35',
            title: 'Priority',
            type: 'status',
            labels: [
              { id: '0', text: 'High', color: statusColorHex('dark_red'), isMondayDone: false },
              { id: '1', text: 'Low', color: statusColorHex('done_green'), isMondayDone: true },
              { id: '2', text: 'Medium', color: statusColorHex('working_orange'), isMondayDone: false },
            ],
          },
          {
            id: 'status',
            title: 'Status',
            type: 'status',
            labels: [
              { id: '0', text: 'Working on it', color: statusColorHex('working_orange'), isMondayDone: false },
              { id: '1', text: 'Done', color: statusColorHex('done_green'), isMondayDone: true },
              { id: '2', text: 'Stuck', color: statusColorHex('stuck_red'), isMondayDone: false },
            ],
          },
        ],
        dueColumns: [
          { id: 'date4', title: 'Due date', type: 'date' },
          { id: 'timeline', title: 'Timeline', type: 'timeline' },
        ],
      };
    },

    /** Writes into the fixture's own store, so the panel round-trips without an api. */
    async setMapping(input): Promise<TaskMappingView> {
      const current = fixtureMapping[input.boardId] ?? {
        boardId: input.boardId,
        assigneeColumnIds: ['people'],
        statusColumnId: 'status',
        dueColumnId: 'date4',
        doneLabels: ['1'],
        overridden: false,
        overriddenBy: null,
        detectedAt: day(-10),
      };
      if (input.reset === true) {
        const reset: TaskMappingView = {
          boardId: input.boardId,
          assigneeColumnIds: ['people'],
          statusColumnId: 'status',
          dueColumnId: 'date4',
          doneLabels: ['1'],
          overridden: false,
          overriddenBy: null,
          detectedAt: day(0),
        };
        fixtureMapping[input.boardId] = reset;
        return reset;
      }
      const next: TaskMappingView = {
        ...current,
        ...(input.assigneeColumnIds === undefined ? {} : { assigneeColumnIds: input.assigneeColumnIds }),
        ...(input.statusColumnId === undefined ? {} : { statusColumnId: input.statusColumnId }),
        ...(input.dueColumnId === undefined ? {} : { dueColumnId: input.dueColumnId }),
        ...(input.doneLabels === undefined ? {} : { doneLabels: input.doneLabels }),
        overridden: true,
        overriddenBy: ME.id,
        detectedAt: null,
      };
      fixtureMapping[input.boardId] = next;
      return next;
    },

    async itemContext({ boardId }): Promise<TaskItemContext> {
      return {
        found: true,
        people: [ME, ADA, GRACE],
        columns: [
          {
            boardId,
            id: 'status',
            type: 'status',
            title: 'Status',
            settingsStr: {
              labels: { '0': 'Working on it', '1': 'Done', '2': 'Stuck' },
              labels_positions_v2: { '0': 0, '1': 1, '2': 2 },
              done_colors: [1],
            },
            width: 140,
            position: '2',
            revision: '1',
          },
          {
            boardId,
            id: 'date4',
            type: 'date',
            title: 'Due date',
            settingsStr: {},
            width: 140,
            position: '3',
            revision: '1',
          },
          {
            boardId,
            id: 'people',
            type: 'people',
            title: 'Owner',
            settingsStr: {},
            width: 140,
            position: '4',
            revision: '1',
          },
        ],
        values: {
          status: { index: 0 },
          date4: { date: '2026-09-15' },
          people: { personsAndTeams: [{ id: Number(ME.id), kind: 'person' }] },
        },
      };
    },

    async setCell(): Promise<unknown> {
      return { changed: true };
    },

    async bulkSetCell(input): Promise<{ changed: number; refused: never[] }> {
      return { changed: input.itemIds.length, refused: [] };
    },

    /**
     * **P-301** — `tasks.archive`. It answers the way the service does: rows that moved, and the
     * items it would not touch. The fixture's "would not touch" is an item it has never heard
     * of, which is the closest a source with no permission tables can come to the server's
     * "neither yours nor yours to delegate".
     */
    async archiveTasks({ itemIds }) {
      const known = new Set([...MINE, ...ASSIGNED, ...ARCHIVE].map((entry) => entry.itemId));
      let changed = 0;
      const skipped: string[] = [];
      for (const itemId of itemIds) {
        if (!known.has(itemId)) {
          skipped.push(itemId);
          continue;
        }
        if (archivedAt[itemId] !== undefined) continue;
        archivedAt[itemId] = day(0);
        changed += 1;
      }
      return { changed, skipped };
    },

    async unarchiveTasks({ itemIds }) {
      const known = new Set([...MINE, ...ASSIGNED, ...ARCHIVE].map((entry) => entry.itemId));
      let changed = 0;
      const skipped: string[] = [];
      for (const itemId of itemIds) {
        if (!known.has(itemId)) {
          skipped.push(itemId);
          continue;
        }
        if (archivedAt[itemId] === undefined) continue;
        delete archivedAt[itemId];
        changed += 1;
      }
      return { changed, skipped };
    },
  };
}

/* ── the two panels' fixture (round T4) ───────────────────────────────────── */

/**
 * The same rows, counted, so `/__ui/tasks?screen=insights` and `?screen=timeline` can be
 * rendered — and therefore *screenshotted on a phone* — with no api and no database.
 *
 * Round T3 built the panels against `tasks.insights` / `tasks.timeline` and left `source?` as a
 * parameter for exactly this. The arithmetic is the **fixture's own**, not a second
 * implementation of the service: it counts the rows it is handed and nothing else, so that what
 * the panel paints can be read against what is in this file.
 */
function insightsFor(rows: readonly TaskRowView[], subject: 'mine' | 'assigned'): TaskInsightsView {
  const done = rows.filter((entry) => entry.isDone);
  const overdue = rows.filter((entry) => entry.bucket === 'overdue' && !entry.isDone);
  const peopleKey = (entry: TaskRowView) => (subject === 'assigned' ? entry.assignee : entry.assigner);

  const members = new Map<string, TaskMemberInsightView>();
  for (const entry of rows) {
    const person = peopleKey(entry);
    const id = person?.id ?? 'none';
    const current =
      members.get(id) ??
      ({
        user: person ?? null,
        open: 0,
        done: 0,
        overdue: 0,
        completionRate: 0,
        onTimeRate: 0,
        averageDaysToComplete: null,
        averageDaysOverdue: null,
        share: 0,
        atRisk: 0,
        missingAccess: 0,
      } satisfies TaskMemberInsightView);
    if (entry.isDone) current.done += 1;
    else current.open += 1;
    if (entry.bucket === 'overdue' && !entry.isDone) current.overdue += 1;
    if (!entry.assigneeHasAccess) current.missingAccess += 1;
    members.set(id, current);
  }
  for (const member of members.values()) {
    const total = member.open + member.done;
    member.completionRate = total === 0 ? 0 : Math.round((member.done / total) * 1000) / 1000;
    member.onTimeRate = total === 0 ? 0 : Math.round(((total - member.overdue) / total) * 1000) / 1000;
    member.averageDaysToComplete = member.done === 0 ? null : 3.4;
    member.averageDaysOverdue = member.overdue === 0 ? null : 5.2;
    member.share = rows.length === 0 ? 0 : Math.round((total / rows.length) * 1000) / 1000;
    member.atRisk = member.overdue;
  }

  const boards = new Map<string, { boardId: string; boardName: string; open: number; done: number; overdue: number }>();
  for (const entry of rows) {
    const current = boards.get(entry.boardId) ?? {
      boardId: entry.boardId,
      boardName: entry.boardName,
      open: 0,
      done: 0,
      overdue: 0,
    };
    if (entry.isDone) current.done += 1;
    else current.open += 1;
    if (entry.bucket === 'overdue' && !entry.isDone) current.overdue += 1;
    boards.set(entry.boardId, current);
  }

  const trend = ['2026-W35', '2026-W36', '2026-W37', '2026-W38'].map((week, index) => ({
    week,
    assigned: 4 + index * 2,
    done: 2 + index,
  }));

  return {
    range: { from: day(-30), to: day(0) },
    total: rows.length,
    open: rows.length - done.length,
    done: done.length,
    overdue: overdue.length,
    completionRate: rows.length === 0 ? 0 : Math.round((done.length / rows.length) * 1000) / 1000,
    onTimeRate: rows.length === 0 ? 0 : Math.round(((rows.length - overdue.length) / rows.length) * 1000) / 1000,
    averageDaysToComplete: 3.4,
    members: [...members.values()].sort((a, b) => b.open + b.done - (a.open + a.done)),
    boards: [...boards.values()],
    trend,
    atRisk: overdue.slice(0, 6),
  };
}

function timelineFor(rows: readonly TaskRowView[], subject: 'mine' | 'assigned'): TaskTimelineView {
  const lanes = new Map<string, { key: string; label: string; rows: TaskRowView[] }>();
  for (const entry of rows) {
    if (entry.dueAt === null) continue;
    const key = subject === 'assigned' ? (entry.assignee?.id ?? 'none') : entry.boardId;
    const label = subject === 'assigned' ? (entry.assignee?.name ?? 'Unassigned') : entry.boardName;
    const lane = lanes.get(key) ?? { key, label, rows: [] };
    lane.rows.push(entry);
    lanes.set(key, lane);
  }
  return { range: { from: day(-14), to: day(21) }, lanes: [...lanes.values()] };
}

/** The `TasksInsightsSource` the harness hands the two panels (`?screen=insights|timeline`). */
export function createFixtureInsightsSource(options: FixtureTasksOptions = {}): TasksInsightsSource {
  const rowsFor = (tab: 'mine' | 'assigned') =>
    options.empty ? [] : tab === 'assigned' ? ASSIGNED : MINE;
  return {
    async insights({ tab }) {
      return insightsFor(rowsFor(tab), tab);
    },
    async timeline({ tab }) {
      return timelineFor(rowsFor(tab), tab);
    },
    async mapping({ boardId }) {
      return {
        boardId,
        assigneeColumnIds: ['people'],
        statusColumnId: 'status',
        dueColumnId: 'date4',
        doneLabels: ['1'],
        overridden: false,
        overriddenBy: null,
        detectedAt: day(-10),
      };
    },
    async itemCells({ boardId }) {
      return {
        found: true,
        people: [ME, ADA, GRACE, KATE],
        columns: [
          {
            boardId,
            id: 'date4',
            type: 'date',
            title: 'Due date',
            settingsStr: {},
            width: 140,
            position: '3',
            revision: '1',
          },
        ],
        values: { date4: { date: '2026-09-15' } },
      };
    },
    async setCell() {
      return { changed: true };
    },
  };
}
